// The code-generated drum kits (engine `kitgen`): ids, display names and which pads play which
// role in breaks, dawn bars and Wizard grooves. The kit's own `kit.json` (written by
// `npm run gen-kits`) holds the files, default levels and choke groups.

import type { BreakRoles } from "./generated/BreakRoles";

export type KitId =
  "jungle" | "deep_sea" | "trap_lab" | "deep_house" | "techno" | "disco" | "garage" | "afro" | "synthwave";
export const KIT_IDS: readonly KitId[] = [
  "jungle",
  "deep_sea",
  "trap_lab",
  "deep_house",
  "techno",
  "disco",
  "garage",
  "afro",
  "synthwave",
];

export type KitStyle = "breakbeat" | "dub" | "halftime" | "four_floor";

export interface KitInfo {
  id: KitId;
  name: string;
  /** The genre the kit is voiced for (shown with the day). */
  genre: string;
  /** Zoo-skin habitat behind the creatures. */
  habitat: "jungle" | "deep_sea";
  style: KitStyle;
  roles: BreakRoles;
  /** Extra pads grooves may use (shakers, percussion), in order of preference. */
  extras: number[];
  /** Pads with a pitch (kept for display; drum pads aren't key-locked). */
  pitched: number[];
}

export const KITS: Record<KitId, KitInfo> = {
  // Pads: (Jungle) 0 kick, 1 snare, 2 rim, 3 clap, 4 hat, 5 open hat, 6 shaker, 7 low tom, 8 hi tom, 9 kick
  // punch, 10 snare snap, 11 kick tight, 12 snare crack, 13 snare tight, 14 snare room, 15 snare ghost; (Deep
  // Sea) 0 kick, 1 snare, 2 rim, 3 clap, 4 hat, 5 open hat, 6 shaker, 7 low tom, 8 hi tom, 9 snare snap, 10
  // kick deep, 11 kick dusty, 12 snare dub, 13 snare dusty, 14 snare brush, 15 snare ghost.
  jungle: {
    id: "jungle",
    genre: "Jungle / breakbeat",
    name: "Jungle",
    habitat: "jungle",
    style: "breakbeat",
    roles: { kick: 0, snare: 1, closed_hat: 4, open_hat: 5, fill_low: 7, fill_high: 8, accent: 5 },
    extras: [6, 2, 3],
    pitched: [],
  },
  deep_sea: {
    id: "deep_sea",
    genre: "Dub / downtempo",
    name: "Deep Sea",
    habitat: "deep_sea",
    style: "dub",
    roles: { kick: 0, snare: 1, closed_hat: 4, open_hat: 5, fill_low: 7, fill_high: 8, accent: 5 },
    extras: [6, 2, 3],
    pitched: [],
  },
  // Pads: 0 kick, 1 kick hard, 2 kick sub, 3 snare crack, 4 snare, 5 snare tight, 6 clap, 7 clap stack, 8
  // snap, 9 hat, 10 hat tick, 11 hat loose, 12 open hat, 13 rim, 14 wood perc, 15 crash.
  trap_lab: {
    id: "trap_lab",
    genre: "Trap",
    name: "Trap Lab",
    habitat: "deep_sea",
    style: "halftime",
    roles: { kick: 0, snare: 4, closed_hat: 9, open_hat: 12, fill_low: 5, fill_high: 14, accent: 15 },
    extras: [10, 8, 13],
    pitched: [],
  },
  // Pads: 0 kick, 1 kick punch, 2 snare short, 3 clap, 4 clap tight, 5 snare, 6 snare snap, 7 rim, 8 hat, 9
  // hat soft, 10 open hat, 11 shaker, 12 ride, 13 conga low, 14 conga high, 15 tambourine.
  deep_house: {
    id: "deep_house",
    genre: "Deep house",
    name: "Deep House",
    habitat: "jungle",
    style: "four_floor",
    roles: { kick: 0, snare: 3, closed_hat: 8, open_hat: 10, fill_low: 13, fill_high: 14, accent: 12 },
    extras: [11, 15, 7],
    pitched: [],
  },
  // Pads: 0 kick, 1 rumble kick, 2 kick hard, 3 clap, 4 snare, 5 rim, 6 hat, 7 hat ghost, 8 open hat, 9 ride,
  // 10 crash, 11 tom low, 12 tom mid, 13 metal perc, 14 snare industrial, 15 snare short.
  techno: {
    id: "techno",
    genre: "Techno",
    name: "Warehouse Techno",
    habitat: "deep_sea",
    style: "four_floor",
    roles: { kick: 0, snare: 3, closed_hat: 6, open_hat: 8, fill_low: 11, fill_high: 12, accent: 10 },
    extras: [9, 13, 5],
    pitched: [],
  },
  // Pads: 0 kick, 1 kick dry, 2 snare, 3 snare ghost, 4 claps, 5 hat, 6 hat pedal, 7 open hat, 8 ride bell, 9
  // crash, 10 tom low, 11 tom mid, 12 snare tight, 13 cowbell, 14 tambourine, 15 conga.
  disco: {
    id: "disco",
    genre: "Nu-disco / funk",
    name: "Disco Strut",
    habitat: "jungle",
    style: "four_floor",
    roles: { kick: 0, snare: 2, closed_hat: 5, open_hat: 7, fill_low: 10, fill_high: 11, accent: 9 },
    extras: [14, 4, 15],
    pitched: [],
  },
  // Pads: 0 kick, 1 kick sub, 2 snare, 3 rim shot, 4 clap, 5 snap, 6 hat, 7 hat soft, 8 open hat, 9 shaker,
  // 10 woodblock, 11 bongo low, 12 bongo high, 13 snare snappy, 14 tambourine, 15 snare crisp.
  garage: {
    id: "garage",
    genre: "UK garage / 2-step",
    name: "Garage Shuffle",
    habitat: "jungle",
    style: "breakbeat",
    roles: { kick: 0, snare: 2, closed_hat: 6, open_hat: 8, fill_low: 11, fill_high: 12, accent: 14 },
    extras: [9, 3, 10],
    pitched: [],
  },
  // Pads: 0 kick, 1 kick tight, 2 clap, 3 side stick, 4 hat, 5 open hat, 6 shaker, 7 snare tight, 8 conga
  // low, 9 conga high, 10 conga slap, 11 djembe bass, 12 djembe tone, 13 djembe slap, 14 kick deep, 15 agogo.
  afro: {
    id: "afro",
    genre: "Afro house",
    name: "Afro Pulse",
    habitat: "jungle",
    style: "four_floor",
    roles: { kick: 0, snare: 2, closed_hat: 4, open_hat: 5, fill_low: 8, fill_high: 9, accent: 15 },
    extras: [6, 3, 12],
    pitched: [],
  },
  // Pads: 0 kick, 1 kick big, 2 gated snare, 3 snare dry, 4 gated clap, 5 hat, 6 open hat, 7 tambourine, 8
  // crash, 9 synth tom low, 10 synth tom mid, 11 synth tom high, 12 snare snap, 13 rim, 14 shaker, 15 kick
  // drive.
  synthwave: {
    id: "synthwave",
    genre: "Synthwave",
    name: "Synthwave",
    habitat: "deep_sea",
    style: "four_floor",
    roles: { kick: 0, snare: 2, closed_hat: 5, open_hat: 6, fill_low: 9, fill_high: 11, accent: 8 },
    extras: [7, 14, 4],
    pitched: [],
  },
};

export function isKitId(x: string): x is KitId {
  return (KIT_IDS as readonly string[]).includes(x);
}

/** Sample id of a kit pad file, as the engine resolves it (`kit:<kit>/<file>`). */
export function kitSampleId(kit: KitId, file: string): string {
  return `kit:${kit}/${file}`;
}

/** URL of a kit file below the web root. */
export function kitFileUrl(kit: KitId, file: string): string {
  return `/kits/${kit}/${file}`;
}
